import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { RequireAuth } from "@/components/RequireAuth";
import { RequireRole } from "@/components/RequireRole";
import { RequireFeature } from "@/components/RequireFeature";
import { AppLayout } from "@/components/AppLayout";
import { AppToaster } from "@/components/AppToaster";
import { EnvBanner } from "@/components/EnvBanner";
import { IdleGuard } from "@/components/IdleGuard";
import Dashboard from "@/pages/Dashboard";
import Setup from "@/pages/Setup";
import Templates from "@/pages/Templates";
import TemplateEditor from "@/pages/TemplateEditor";
import Forms from "@/pages/Forms";
import FillForm from "@/pages/FillForm";
import Resources from "@/pages/Resources";
import Submissions from "@/pages/Submissions";
import SubmissionDetail from "@/pages/SubmissionDetail";
import AcknowledgementDetail from "@/pages/AcknowledgementDetail";
import AccountSettings from "@/pages/AccountSettings";
import MyCrew from "@/pages/MyCrew";
import Tasks from "@/pages/Tasks";
import Analytics from "@/pages/Analytics";
import Activity from "@/pages/Activity";
import Platform from "@/pages/Platform";
import PlatformCompany from "@/pages/PlatformCompany";
import PlatformCompanySetup from "@/pages/PlatformCompanySetup";
import Settings from "@/pages/Settings";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import Contractors from "@/pages/Contractors";
import PortalPage from "@/pages/PortalPage";
import UrgentQueue from "@/pages/UrgentQueue";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <EnvBanner />
        <AppRoutes />
        <AppToaster />
        <IdleGuard />
      </BrowserRouter>
    </AuthProvider>
  );
}

// Route tree: public auth + portal routes, then role/feature-guarded app routes.
function AppRoutes() {
  return (
    <Routes>
          {/* Public auth routes — accounts are invitation-only; no self-registration */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/verify-email" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          {/* Public QR contractor portal — no login, token-scoped server-side */}
          <Route path="/portal/:token" element={<PortalPage />} />

          {/* Protected app routes */}
          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              {/* Personal account settings — every signed-in user */}
              <Route path="/account" element={<AccountSettings />} />

              {/* Super-Admin only */}
              <Route element={<RequireRole allow={["super_admin"]} />}>
                <Route path="/platform" element={<Platform />} />
                <Route path="/platform/company/:id" element={<PlatformCompany />} />
                <Route path="/platform/company/:id/setup" element={<PlatformCompanySetup />} />
              </Route>

              {/* Super-Admin only: template curation */}
              <Route element={<RequireRole allow={["super_admin"]} />}>
                <Route path="/templates" element={<Templates />} />
                <Route path="/templates/:id" element={<TemplateEditor />} />
              </Route>

              {/* Org Admin only */}
              <Route element={<RequireRole allow={["org_admin"]} />}>
                <Route path="/setup" element={<Setup />} />
                <Route path="/settings" element={<Settings />} />
                <Route element={<RequireFeature feature="contractors" />}>
                  <Route path="/contractors" element={<Contractors />} />
                </Route>
              </Route>

              <Route element={<RequireRole allow={["super_admin", "org_admin"]} />}>
                <Route path="/activity" element={<Activity />} />
              </Route>

              {/* Org Admin + Supervisor + Field Employee */}
              <Route element={<RequireRole allow={["org_admin", "supervisor", "field_employee"]} />}>
                <Route path="/forms" element={<Forms />} />
                <Route path="/forms/fill/:templateId" element={<FillForm />} />
                <Route element={<RequireFeature feature="safety_bulletins" />}>
                  <Route path="/resources" element={<Resources />} />
                </Route>
              </Route>

              {/* Org Admin + Supervisor */}
              <Route element={<RequireRole allow={["org_admin", "supervisor"]} />}>
                <Route path="/urgent" element={<UrgentQueue />} />
                <Route path="/submissions" element={<Submissions />} />
                <Route path="/submissions/:id" element={<SubmissionDetail />} />
                <Route path="/acknowledgements/:id" element={<AcknowledgementDetail />} />
                <Route path="/my-crew" element={<MyCrew />} />
              </Route>

              {/* Tasks: assigned to any active user (worker sees their own tasks). */}
              <Route element={<RequireRole allow={["org_admin", "supervisor", "field_employee"]} />}>
                <Route path="/tasks" element={<Tasks />} />
              </Route>

              {/* Org Admin (+ Super-Admin cross-company) */}
              <Route element={<RequireRole allow={["org_admin"]} />}>
                <Route element={<RequireFeature feature="analytics" />}>
                  <Route path="/analytics" element={<Analytics />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Routes>
  );
}

export default App;
